import { useEffect, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import * as Sentry from "@sentry/react";
import { toast } from "@/hooks/use-toast";
import { api } from "../../../convex/_generated/api";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, PillTag, Btn, SectionTitle } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";

type Phase = {
  key: "menstrual" | "follicular" | "ovulatory" | "luteal";
  label: string;
  color: string;
  bg: string;
  emoji: string;
};

const PHASE_META: Phase[] = [
  { key: "menstrual",  label: "Menstrual",  color: "#A86638", bg: "rgba(168,102,56,0.10)",  emoji: "🌑" },
  { key: "follicular", label: "Follicular", color: "#7F77DD", bg: "rgba(127,119,221,0.10)", emoji: "🌱" },
  { key: "ovulatory",  label: "Ovulatory",  color: "#D4A04E", bg: "rgba(212,160,78,0.10)",  emoji: "☀️" },
  { key: "luteal",     label: "Luteal",     color: "#1E4A35", bg: "rgba(30,74,53,0.08)",     emoji: "🌙" },
];

/** Compute the four phase day ranges for a custom cycle length. */
function phaseRanges(cycleLength: number) {
  const menstrualEnd = 5;
  const ovulationDay = Math.round(cycleLength / 2); // 14 for 28, 15 for 30, etc.
  const ovuStart = ovulationDay - 1;
  const ovuEnd = ovulationDay + 1;
  return {
    menstrual:  { start: 1,            end: menstrualEnd },
    follicular: { start: menstrualEnd + 1, end: ovuStart - 1 },
    ovulatory:  { start: ovuStart,     end: ovuEnd },
    luteal:     { start: ovuEnd + 1,   end: cycleLength },
  };
}

function phaseForDay(day: number, cycleLength: number): Phase {
  const r = phaseRanges(cycleLength);
  if (day <= r.menstrual.end) return PHASE_META[0];
  if (day <= r.follicular.end) return PHASE_META[1];
  if (day <= r.ovulatory.end) return PHASE_META[2];
  return PHASE_META[3];
}

function rangeLabel(r: { start: number; end: number }) {
  return r.start === r.end ? `${r.start}` : `${r.start}–${r.end}`;
}

function todayKey(): string {
  return new Date().toLocaleDateString("en-CA");
}

function isLengthInputValidAndChanged(raw: string, current: number): boolean {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 20 || n > 45) return false;
  return n !== current;
}

export default function Cycle() {
  const settings = useQuery(api.cycle.getSettings, { todayKey: todayKey() });
  const setCycleLengthMutation = useMutation(api.cycle.setCycleLength);
  const logPeriodMutation = useMutation(api.cycle.logPeriodStart);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [lengthInput, setLengthInput] = useState(() => String(settings?.cycleLength ?? 28));
  const [periodInput, setPeriodInput] = useState(() => settings?.lastPeriodStart ?? todayKey());
  const [emptyDateInput, setEmptyDateInput] = useState(() => todayKey());
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState<"length" | "period" | null>(null);

  const cycleLength = settings?.cycleLength ?? 28;
  const currentDay = settings?.currentDay ?? null;
  const lastPeriodStart = settings?.lastPeriodStart ?? null;
  const ranges = phaseRanges(cycleLength);
  const currentPhase = currentDay != null ? phaseForDay(currentDay, cycleLength) : PHASE_META[1];
  const daysUntilNext = currentDay != null ? Math.max(0, cycleLength - currentDay + 1) : null;

  // Keep inputs synced when settings hydrate from Convex.
  useEffect(() => {
    if (settings?.cycleLength) setLengthInput(String(settings.cycleLength));
    if (settings?.lastPeriodStart) setPeriodInput(settings.lastPeriodStart);
  }, [settings?.cycleLength, settings?.lastPeriodStart]);

  // Auto-clear the saved chip after 2s.
  useEffect(() => {
    if (!savedFlash) return;
    const t = setTimeout(() => setSavedFlash(null), 2000);
    return () => clearTimeout(t);
  }, [savedFlash]);

  const openSettings = () => {
    setLengthInput(String(cycleLength));
    setPeriodInput(lastPeriodStart ?? todayKey());
    setSettingsOpen(true);
  };

  // Both handlers swallow nothing: they log, report, and tell her out loud.
  // Neither ever rejects, so calling them from onClick can't leak a rejection.
  const handleSaveLength = async (): Promise<void> => {
    const n = parseInt(lengthInput, 10);
    if (!Number.isFinite(n) || n < 20 || n > 45) return;
    setSaving(true);
    try {
      await setCycleLengthMutation({ cycleLength: n });
      setSavedFlash("length");
    } catch (err) {
      console.error("[Cycle] saving cycle length failed", err);
      Sentry.captureException(err, { tags: { subsystem: "cycle" } });
      setSavedFlash(null);
      setLengthInput(String(cycleLength)); // show what's actually saved, not what she typed
      toast({
        variant: "destructive",
        title: "We couldn't save your cycle length",
        description: "Nothing changed. Check your connection and try saving it again.",
      });
    } finally { setSaving(false); }
  };

  const handleLogPeriod = async (dateKey: string): Promise<void> => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return;
    setSaving(true);
    try {
      await logPeriodMutation({ dateKey });
      setSavedFlash("period");
    } catch (err) {
      console.error("[Cycle] logging period start failed", err);
      Sentry.captureException(err, { tags: { subsystem: "cycle" } });
      setSavedFlash(null);
      setPeriodInput(lastPeriodStart ?? todayKey()); // roll back to the date actually on record
      toast({
        variant: "destructive",
        title: "We couldn't log your period",
        description: "That date wasn't saved, so your cycle day hasn't moved. Check your connection and log it again.",
      });
    } finally { setSaving(false); }
  };

  const headlines: Record<Phase["key"], { title: React.ReactNode; subtitle: string; pill: string }> = {
    menstrual:  { title: <>Your body is<br /><span className="italic">resting deep.</span></>, subtitle: "Slow the pace. Iron-rich meals. Permission to rest.", pill: "Menstrual window" },
    follicular: { title: <>Your body is<br /><span className="italic">building.</span></>,    subtitle: "Energy returning. Try the new lift. Plan the week.", pill: "Follicular window" },
    ovulatory:  { title: <>Your body is<br /><span className="italic">in bloom.</span></>,    subtitle: "Energy is climbing. Train heavier, eat warmer, lead bravely.", pill: "Ovulatory window" },
    luteal:     { title: <>Your body is<br /><span className="italic">winding down.</span></>, subtitle: "Steady, warm meals. Walking + yoga. Tend tasks.", pill: "Luteal window" },
  };
  const headline = headlines[currentPhase.key];

  return (
    <div className="pb-6">
      <ScreenHeader
        variant="forest"
        eyebrow={currentDay != null ? `Cycle · Day ${currentDay} · ${currentPhase.label}` : `Cycle · Not set up`}
        title={currentDay != null ? headline.title : <>Tell us where<br /><span className="italic">you are.</span></>}
        subtitle={currentDay != null ? headline.subtitle : "Log when your period last started so we can tune today for you."}
        trailing={
          <button
            onClick={openSettings}
            className="rounded-full flex items-center justify-center"
            style={{ width: 36, height: 36, background: "rgba(245,240,232,0.14)", border: "1px solid rgba(245,240,232,0.18)" }}
            data-testid="button-cycle-settings"
            aria-label="Cycle settings"
          >
            <Icon name="tune" size={19} />
          </button>
        }
      >
        {currentDay != null && (
          <div className="flex items-center gap-2 flex-wrap">
            <PillTag tone="gold">{headline.pill}</PillTag>
            {daysUntilNext != null && <PillTag tone="purple">Next period · {daysUntilNext} days</PillTag>}
          </div>
        )}
      </ScreenHeader>

      <div className="px-5 -mt-3 space-y-4">
        {currentDay == null ? (
          <GlassCard padding={20}>
            <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55, marginBottom: 12 }}>
              Tell us the first day of your most recent period — we&apos;ll use that and your cycle length to tune every day. New here mid-month? Just pick the day it actually started.
            </p>
            <label className="mnf-eyebrow" style={{ display: "block", marginBottom: 4 }}>Period start date</label>
            <input
              type="date"
              value={emptyDateInput}
              max={todayKey()}
              onChange={(e) => setEmptyDateInput(e.target.value)}
              className="mnf-body"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(30,74,53,0.16)", background: "rgba(255,255,255,0.85)", fontSize: 14, color: "var(--mnf-fd)", outline: "none", marginBottom: 12 }}
            />
            <Btn variant="primary" block size="md" onClick={() => { void handleLogPeriod(emptyDateInput); }} disabled={saving}>
              <Icon name="event" size={16} /> Log this date as my period start
            </Btn>
            <button
              onClick={openSettings}
              className="mnf-body w-full mt-2"
              style={{ background: "none", border: "none", color: "var(--mnf-tg)", fontSize: 12.5, padding: "8px 0", cursor: "pointer" }}
            >
              Or set my cycle length first →
            </button>
            {savedFlash === "period" && (
              <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-f)", marginTop: 8, fontWeight: 700 }}>Saved ✓</p>
            )}
          </GlassCard>
        ) : (
          <GlassCard padding={20}>
            <div className="flex items-center justify-center mb-4">
              <PhaseRing currentDay={currentDay} cycleLength={cycleLength} />
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {PHASE_META.map((p) => {
                const active = p.key === currentPhase.key;
                const r = ranges[p.key];
                return (
                  <div key={p.key}
                    className="flex flex-col items-center gap-1 p-2"
                    style={{
                      borderRadius: 14,
                      background: active ? p.bg : "transparent",
                      border: `1px solid ${active ? p.color + "30" : "transparent"}`,
                    }}>
                    <span style={{ fontSize: 18 }}>{p.emoji}</span>
                    <span className="mnf-body" style={{ fontSize: 10, fontWeight: active ? 700 : 500, color: active ? p.color : "var(--mnf-tg)", textAlign: "center", lineHeight: 1.2 }}>
                      {p.label}
                    </span>
                    <span className="mnf-body" style={{ fontSize: 9, color: "var(--mnf-tg)" }}>{rangeLabel(r)}</span>
                  </div>
                );
              })}
            </div>
          </GlassCard>
        )}

        <SectionTitle eyebrow="Tuned for today" title="What your body needs" />
        <div className="grid grid-cols-2 gap-3">
          <TuningCard icon="fitness_center" tone="forest" label="Training" value={trainingFor(currentPhase.key)} sub={trainingSubFor(currentPhase.key)} />
          <TuningCard icon="local_fire_department" tone="coral" label="Fueling" value={fuelingFor(currentPhase.key)} sub={fuelingSubFor(currentPhase.key)} />
          <TuningCard icon="self_improvement" tone="purple" label="Mood" value={moodFor(currentPhase.key)} sub={moodSubFor(currentPhase.key)} />
          <TuningCard icon="bedtime" tone="gold" label="Sleep" value={sleepFor(currentPhase.key)} sub={sleepSubFor(currentPhase.key)} />
        </div>

        <GlassCard organic padding={20} style={{ background: "linear-gradient(135deg, rgba(212,160,78,0.10) 0%, rgba(192,120,74,0.10) 100%)" }}>
          <div className="flex items-start gap-3">
            <span style={{ fontSize: 28 }} aria-hidden>{currentPhase.emoji}</span>
            <div className="flex-1">
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-gold)" }}>Coach Kiley · Grace note</p>
              <p className="mnf-serif" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.3, marginTop: 6 }}>
                {graceVerseFor(currentPhase.key)}
              </p>
              <p className="mnf-body italic mt-2" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
                {graceNoteFor(currentPhase.key)}
              </p>
            </div>
          </div>
        </GlassCard>

        <SectionTitle eyebrow="Through the month" title="What each phase asks of you" />
        <div className="space-y-3">
          {[
            { phase: "Menstrual",  body: "Slow movement. Iron-rich meals. Permission to rest. Psalm 23 territory.", color: "#A86638" },
            { phase: "Follicular", body: "Build week. Try the new workout. Plan the family meals. Energy returns.", color: "#7F77DD" },
            { phase: "Ovulatory", body: "Peak strength + connection. Heavy lifts. Lead, host, send the email.",   color: "#D4A04E" },
            { phase: "Luteal",     body: "Steady, warm meals. Walking + yoga. Tend tasks. Honor the rising tide.",  color: "#1E4A35" },
          ].map((p, i) => (
            <GlassCard key={i} padding={14}>
              <div className="flex items-start gap-3">
                <span style={{ width: 4, alignSelf: "stretch", borderRadius: 999, background: p.color, marginTop: 2, marginBottom: 2 }} />
                <div className="flex-1">
                  <p className="mnf-body" style={{ fontSize: 13, fontWeight: 700, color: "var(--mnf-fd)" }}>{p.phase}</p>
                  <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55, marginTop: 3 }}>{p.body}</p>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>

        {currentDay != null && (
          <>
            <Btn variant="secondary" block size="md" onClick={() => { void handleLogPeriod(todayKey()); }} disabled={saving}>
              <Icon name="event" size={16} /> My period started today
            </Btn>
            {savedFlash === "period" && (
              <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-f)", textAlign: "center", marginTop: 6, fontWeight: 700 }}>Saved ✓ — recalculating your day</p>
            )}
          </>
        )}
      </div>

      {/* Settings sheet */}
      {settingsOpen && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 150, display: "flex", alignItems: "flex-end" }}
          onClick={() => setSettingsOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "var(--mnf-cr, #F5F0E8)", borderRadius: "24px 24px 0 0", padding: "24px 20px 32px", width: "100%", maxWidth: 480, margin: "0 auto", maxHeight: "70vh", overflowY: "auto" }}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="mnf-eyebrow">Cycle settings</p>
              <button onClick={() => setSettingsOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }} aria-label="Close">
                <Icon name="close" size={18} color="var(--mnf-tg)" />
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
                  <p className="mnf-body" style={{ fontSize: 13, fontWeight: 600, color: "var(--mnf-fd)" }}>Cycle length</p>
                  {savedFlash === "length" && <span className="mnf-body" style={{ fontSize: 11, fontWeight: 700, color: "var(--mnf-f)" }}>Saved ✓</span>}
                </div>
                <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginBottom: 8 }}>Average days from period start to next start. Typical range 21–35.</p>
                <input
                  type="number"
                  value={lengthInput}
                  onChange={(e) => setLengthInput(e.target.value)}
                  min={20} max={45}
                  className="mnf-body"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(30,74,53,0.16)", background: "rgba(255,255,255,0.85)", fontSize: 14, color: "var(--mnf-fd)", outline: "none", marginBottom: 8 }}
                />
                <Btn
                  variant="primary"
                  block
                  size="md"
                  onClick={() => { void handleSaveLength(); }}
                  disabled={saving || !isLengthInputValidAndChanged(lengthInput, cycleLength)}
                >
                  Save cycle length
                </Btn>
              </div>

              <div style={{ borderTop: "1px solid rgba(30,74,53,0.08)", paddingTop: 16 }}>
                <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
                  <p className="mnf-body" style={{ fontSize: 13, fontWeight: 600, color: "var(--mnf-fd)" }}>Period start date</p>
                  {savedFlash === "period" && <span className="mnf-body" style={{ fontSize: 11, fontWeight: 700, color: "var(--mnf-f)" }}>Saved ✓</span>}
                </div>
                <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginBottom: 8 }}>
                  {lastPeriodStart ? `Currently logged: ${lastPeriodStart}. Pick a new date to change it.` : "Pick the first day of your most recent period."}
                </p>
                <input
                  type="date"
                  value={periodInput}
                  max={todayKey()}
                  onChange={(e) => setPeriodInput(e.target.value)}
                  className="mnf-body"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(30,74,53,0.16)", background: "rgba(255,255,255,0.85)", fontSize: 14, color: "var(--mnf-fd)", outline: "none", marginBottom: 8 }}
                />
                <Btn
                  variant="primary"
                  block
                  size="md"
                  onClick={() => { void handleLogPeriod(periodInput); }}
                  disabled={saving || periodInput === lastPeriodStart}
                >
                  <Icon name="event" size={16} /> Save period start
                </Btn>
                <button
                  onClick={() => { setPeriodInput(todayKey()); void handleLogPeriod(todayKey()); }}
                  disabled={saving}
                  className="mnf-body w-full"
                  style={{ background: "none", border: "none", color: "var(--mnf-tg)", fontSize: 12, padding: "8px 0 0", cursor: "pointer", textDecoration: "underline" }}
                >
                  Or log today as start →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------------- phase ring -------------------------------- */
function PhaseRing({ currentDay, cycleLength }: { currentDay: number; cycleLength: number }) {
  const size = 200;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const ranges = phaseRanges(cycleLength);

  const arcs = [
    { start: 0,                          end: ranges.menstrual.end,  color: "#A86638" },
    { start: ranges.menstrual.end,       end: ranges.follicular.end, color: "#7F77DD" },
    { start: ranges.follicular.end,      end: ranges.ovulatory.end,  color: "#D4A04E" },
    { start: ranges.ovulatory.end,       end: cycleLength,           color: "#1E4A35" },
  ];
  const polar = (day: number) => {
    const angle = (day / cycleLength) * 2 * Math.PI - Math.PI / 2;
    return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
  };
  const arc = (start: number, end: number, color: string) => {
    const [sx, sy] = polar(start);
    const [ex, ey] = polar(end);
    const large = (end - start) > cycleLength / 2 ? 1 : 0;
    return (
      <path
        d={`M ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey}`}
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        fill="none"
        opacity={0.85}
      />
    );
  };
  const [px, py] = polar(currentDay);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        {arcs.map((a, i) => <g key={i}>{arc(a.start, a.end, a.color)}</g>)}
        <circle cx={px} cy={py} r={10} fill="#fff" stroke="#D4A04E" strokeWidth={3} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="mnf-eyebrow" style={{ color: "var(--mnf-tg)" }}>Day</span>
        <span className="mnf-serif" style={{ fontSize: 56, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1 }}>{currentDay}</span>
        <span className="mnf-body italic" style={{ fontSize: 12, color: "#D4A04E", marginTop: 4 }}>of {cycleLength}</span>
      </div>
    </div>
  );
}

function TuningCard({ icon, tone, label, value, sub }: { icon: string; tone: "forest" | "coral" | "purple" | "gold"; label: string; value: string; sub: string }) {
  const colors: Record<string, { c: string; bg: string }> = {
    forest: { c: "var(--mnf-f)",      bg: "rgba(30,74,53,0.08)" },
    coral:  { c: "var(--mnf-t)",      bg: "rgba(192,120,74,0.10)" },
    purple: { c: "var(--mnf-purple)", bg: "rgba(127,119,221,0.10)" },
    gold:   { c: "var(--mnf-gold)",   bg: "rgba(212,160,78,0.12)" },
  };
  const t = colors[tone];
  return (
    <GlassCard padding={16}>
      <div className="flex items-center justify-center mb-2.5"
        style={{ width: 36, height: 36, borderRadius: 12, background: t.bg, color: t.c }}>
        <Icon name={icon} size={18} weight={500} />
      </div>
      <p className="mnf-eyebrow" style={{ color: t.c }}>{label}</p>
      <p className="mnf-serif" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.2, marginTop: 4 }}>{value}</p>
      <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.45, marginTop: 6 }}>{sub}</p>
    </GlassCard>
  );
}

/* -------------------------- per-phase coach copy -------------------------- */
function trainingFor(k: Phase["key"]): string {
  return k === "menstrual" ? "Gentle"
    : k === "follicular" ? "Build"
    : k === "ovulatory" ? "Heavy lift"
    : "Steady";
}
function trainingSubFor(k: Phase["key"]): string {
  return k === "menstrual" ? "Walk, yoga, slow flow. Listen to your body."
    : k === "follicular" ? "Try the new program. Energy is returning."
    : k === "ovulatory" ? "Push 80–90% intensity. PRs welcome."
    : "Lower intensity. Honor the wind-down.";
}
function fuelingFor(k: Phase["key"]): string {
  return k === "menstrual" ? "Iron + warm"
    : k === "follicular" ? "Whole foods"
    : k === "ovulatory" ? "+200 kcal"
    : "Steady + magnesium";
}
function fuelingSubFor(k: Phase["key"]): string {
  return k === "menstrual" ? "Red meat, leafy greens, warm soups."
    : k === "follicular" ? "Lean protein, vibrant produce, light carbs."
    : k === "ovulatory" ? "Cycle adds need. Add protein at every meal."
    : "Complex carbs, magnesium-rich foods, hydration.";
}
function moodFor(k: Phase["key"]): string {
  return k === "menstrual" ? "Inward"
    : k === "follicular" ? "Curious"
    : k === "ovulatory" ? "Outgoing"
    : "Reflective";
}
function moodSubFor(k: Phase["key"]): string {
  return k === "menstrual" ? "Journal, pray, slow conversations."
    : k === "follicular" ? "Try new things. Schedule the bold ask."
    : k === "ovulatory" ? "Schedule the hard conversation today."
    : "Tend relationships. Finish the small tasks.";
}
function sleepFor(k: Phase["key"]): string {
  return k === "menstrual" ? "8.5 hrs"
    : k === "follicular" ? "8 hrs"
    : k === "ovulatory" ? "7.5 hrs"
    : "8.5 hrs";
}
function sleepSubFor(k: Phase["key"]): string {
  return k === "menstrual" ? "Extra rest is faithful. Warm bath."
    : k === "follicular" ? "Steady bedtime. Morning light."
    : k === "ovulatory" ? "Body temp rises. Cool the bedroom."
    : "Wind down earlier. Limit caffeine.";
}
function graceVerseFor(k: Phase["key"]): string {
  return k === "menstrual" ? "\"Be still and know that I am God.\""
    : k === "follicular" ? "\"Behold, I am doing a new thing.\""
    : k === "ovulatory" ? "\"She is a tree planted by streams of water.\""
    : "\"In quietness and trust is your strength.\"";
}
function graceNoteFor(k: Phase["key"]): string {
  return k === "menstrual" ? "Your body is doing sacred work this week. Slow down without apology, sister. Rest is obedience too."
    : k === "follicular" ? "Energy is rising. Plant the seed of one new habit this week — God meets the willing builder."
    : k === "ovulatory" ? "Your follicular and ovulatory days are God's design for boldness, sister. Train hard. Walk in the sun. Lead with confidence today."
    : "The body is winding down on purpose. Tend the small things, honor the rising tide, and trust the season.";
}
